// Push Notification Utility
const API = process.env.REACT_APP_BACKEND_URL;

// Convert base64 URL safe string to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Helper function to convert ArrayBuffer to base64url
function arrayBufferToBase64url(buffer) {
  if (!buffer) return '';
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  // Convert to base64url (replace + with -, / with _, remove =)
  return window.btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

// Register service worker and subscribe to push
export async function subscribeToPush(token) {
  try {
    // Check if browser supports push notifications
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.log('Push notifications not supported');
      return { success: false, error: 'not_supported' };
    }

    // Request notification permission
    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    if (permission !== 'granted') {
      console.log('Notification permission denied');
      return { success: false, error: 'permission_denied' };
    }

    // Register service worker
    console.log('Registering service worker...');
    const registration = await navigator.serviceWorker.register('/sw.js');
    console.log('Service Worker registered:', registration);

    // Wait for service worker to be ready
    console.log('Waiting for service worker to be ready...');
    await navigator.serviceWorker.ready;
    console.log('Service Worker is ready');

    // Get VAPID public key from server
    console.log('Fetching VAPID key from server...');
    const vapidResponse = await fetch(`${API}/api/push/vapid-key`);
    if (!vapidResponse.ok) {
      throw new Error('Failed to get VAPID key');
    }
    const { publicKey } = await vapidResponse.json();
    console.log('VAPID public key received:', publicKey.substring(0, 20) + '...');

    // Subscribe to push
    console.log('Subscribing to push...');
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey)
    });

    console.log('Push subscription created:', subscription.endpoint);

    // Get subscription keys
    const p256dhKey = subscription.getKey('p256dh');
    const authKey = subscription.getKey('auth');
    
    if (!p256dhKey || !authKey) {
      throw new Error('Failed to get subscription keys');
    }

    const subscriptionData = {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: arrayBufferToBase64url(p256dhKey),
        auth: arrayBufferToBase64url(authKey)
      }
    };
    
    console.log('Sending subscription to server...');
    
    // Send subscription to server
    const response = await fetch(`${API}/api/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(subscriptionData)
    });

    if (response.ok) {
      console.log('Push subscription saved to server successfully');
      return { success: true };
    } else {
      const errorData = await response.text();
      console.error('Server error:', errorData);
      throw new Error('Failed to save subscription to server');
    }

  } catch (error) {
    console.error('Push subscription error:', error);
    return { success: false, error: error.message };
  }
}

// Unsubscribe from push notifications
export async function unsubscribeFromPush(token) {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      await subscription.unsubscribe();
    }

    // Notify server
    await fetch(`${API}/api/push/unsubscribe`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    return { success: true };
  } catch (error) {
    console.error('Push unsubscribe error:', error);
    return { success: false, error: error.message };
  }
}

// Check if push is supported and subscribed
export async function checkPushStatus() {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return { supported: false, subscribed: false };
    }

    const permission = Notification.permission;
    if (permission !== 'granted') {
      return { supported: true, subscribed: false, permission };
    }

    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    return { 
      supported: true, 
      subscribed: !!subscription, 
      permission 
    };
  } catch (error) {
    return { supported: false, subscribed: false, error: error.message };
  }
}
